import React, { useState, useEffect, useRef, useCallback } from 'react';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import axios from 'axios';
import {
  BASE_URL,
  GET_SHORT_BOOKS_LIST,
  GET_SUPPLIER_MASTER,
  MARK_SHORT_BOOKS_ORDERS,
  GET_ITEM_MASTER,
  POST_SHORT_BOOKS_ITEMS,
} from '../utlilities/config';
import Select, { components } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import {
  FiSearch,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiCheck,
  FiX,
  FiFileText,
  FiPlus,
  FiDownload,
} from "react-icons/fi";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading Short Books...</p>
    </div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    fontSize: '0.875rem',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Custom SingleValue component to display supplier name and price
const CustomSingleValue = ({ data, ...props }) => {
  const { label, hasPricing, purchasePrice } = data;
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center justify-between w-full">
        <span className="flex-1 truncate">{label}</span>
        {hasPricing && purchasePrice !== null && purchasePrice !== undefined && (
          <span className="text-xs font-semibold text-indigo-600 ml-2 whitespace-nowrap">
            ₹{parseFloat(purchasePrice).toFixed(2)}
          </span>
        )}
      </div>
    </components.SingleValue>
  );
};

const AddShortBookModal = ({
  isOpen,
  onClose,
  selectedItem,
  onItemChange,
  qty,
  setQty,
  onSubmit,
  loadItemOptions,
  isSubmitting,
}) => {
  const modalRef = useRef(null);
  const qtyInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && qtyInputRef.current && selectedItem) {
      setTimeout(() => {
        qtyInputRef.current?.focus();
        qtyInputRef.current?.select();
      }, 100);
    }
  }, [isOpen, selectedItem]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleCreateItem = (inputValue) => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onItemChange({
      value: null,
      label: trimmed,
      itemName: trimmed,
      isNew: true,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] max-w-md outline-none flex flex-col"
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="bg-indigo-600 text-white px-6 py-3 flex items-center justify-between rounded-t-lg">
          <h3 className="text-lg font-bold">Add to Short Books</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-indigo-700 rounded transition-colors"
            disabled={isSubmitting}
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item <span className="text-red-500">*</span>
            </label>
            <AsyncCreatableSelect
              loadOptions={loadItemOptions}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              defaultOptions
              cacheOptions
              value={selectedItem}
              onChange={onItemChange}
              onCreateOption={handleCreateItem}
              formatCreateLabel={(inputValue) => `Add new item: "${inputValue.trim()}"`}
              placeholder="Search item or type a new name..."
              noOptionsMessage={({ inputValue }) =>
                inputValue && inputValue.length < 2
                  ? 'Type at least 2 characters to search'
                  : inputValue?.trim()
                  ? 'No match — use "Add new item" below'
                  : 'No items found'
              }
              loadingMessage={() => 'Searching items...'}
              styles={customSelectStyles}
              isDisabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              ref={qtyInputRef}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base disabled:bg-gray-100"
              placeholder="Enter quantity"
            />
          </div>
        </div>

        <div className="bg-gray-50 border-t px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            className="px-5 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmit();
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding…' : 'Add to Short Books'}
          </button>
        </div>
      </div>
    </div>
  );
};

const getMostProfitableSupplier = (itemList) => {
  if (!itemList || itemList.length === 0) return null;

  const supplierMap = new Map();
  itemList.forEach((s) => {
    const supplierId = s.supplierID;
    const currentPrice = parseFloat(s.purchasePrice || 0);

    if (!supplierMap.has(supplierId)) {
      supplierMap.set(supplierId, s);
    } else {
      const existing = supplierMap.get(supplierId);
      const existingPrice = parseFloat(existing.purchasePrice || 0);
      if (currentPrice > 0 && (existingPrice === 0 || currentPrice < existingPrice)) {
        supplierMap.set(supplierId, s);
      } else if (currentPrice > 0 && existingPrice === 0) {
        supplierMap.set(supplierId, s);
      }
    }
  });

  const validItems = Array.from(supplierMap.values()).filter((s) => {
    const price = parseFloat(s.purchasePrice || 0);
    return price > 0;
  });

  if (validItems.length > 0) {
    const sorted = [...validItems].sort((a, b) => {
      const priceA = parseFloat(a.purchasePrice || 0);
      const priceB = parseFloat(b.purchasePrice || 0);
      if (priceA !== priceB) return priceA - priceB;
      return String(a.supplierID || '').localeCompare(String(b.supplierID || ''));
    });
    const best = sorted[0];
    return {
      supplierID: best.supplierID,
      supplierName: best.supplierName,
      packing: best.packing,
      foc: best.foc,
      purchasePrice: best.purchasePrice,
    };
  }

  const first = itemList[0];
  return {
    supplierID: first.supplierID,
    supplierName: first.supplierName,
    packing: first.packing,
    foc: first.foc,
    purchasePrice: first.purchasePrice,
  };
};

const ShortBooks = () => {
  const [loading, setLoading] = useState(false);
  const [shortBooksData, setShortBooksData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState({}); // { itemNo: { supplierID, supplierName, packing, foc, purchasePrice } }
  const [quantities, setQuantities] = useState({}); // { itemNo: qty }
  const [selectedItems, setSelectedItems] = useState(new Set()); // Set of selected itemNos
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSelectedItem, setAddSelectedItem] = useState(null);
  const [addQty, setAddQty] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [itemsMaster, setItemsMaster] = useState([]);
  const token = localStorage.getItem('accessToken');
  const itemSearchDebounceRef = useRef(null);
  const isSubmittingAddRef = useRef(false);

  useEffect(() => {
    getShortBooksData();
    getSuppliers();
  }, []);

  useEffect(() => {
    if (!showAddModal || !token) return;

    const fetchInitialItems = async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            XApiKey: process.env.REACT_APP_API_KEY,
            Authorization: `Bearer ${token}`,
            PageNo: 1,
            PageSize: 1000,
            FilterText: '',
            FilterColumn: '0',
            FilterValue: '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          setItemsMaster(pharmacyItems);
        }
      } catch (error) {
        console.error('Failed to fetch items:', error);
      }
    };

    fetchInitialItems();
  }, [showAddModal, token]);

  const mapItemToOption = (item) => ({
    value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
    label: item.Item_Name ?? item.ItemName ?? '',
    itemName: item.Item_Name ?? item.ItemName ?? '',
    isNew: false,
  });

  const loadItemOptions = useCallback(
    (inputValue, callback) => {
      if (itemSearchDebounceRef.current) {
        clearTimeout(itemSearchDebounceRef.current);
      }

      if (!inputValue || inputValue.length < 2) {
        const options = (itemsMaster || []).map(mapItemToOption).filter((opt) => opt.value != null);
        const filteredOptions = inputValue
          ? options.filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
          : options;
        callback(filteredOptions);
        return;
      }

      itemSearchDebounceRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
            headers: {
              XApiKey: process.env.REACT_APP_API_KEY,
              Authorization: `Bearer ${token}`,
              PageNo: 1,
              PageSize: 1000,
              FilterText: inputValue,
              FilterColumn: '0',
              FilterValue: '0',
            },
          });

          if (response.status === 200) {
            const masterData = response.data?.data?.masterData || [];
            const pharmacyItems = masterData.filter(
              (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
            );
            const options = pharmacyItems.map(mapItemToOption).filter((opt) => opt.value != null);
            callback(options);
          } else {
            callback([]);
          }
        } catch (error) {
          console.error('Failed to fetch items:', error);
          callback([]);
        }
      }, 300);
    },
    [itemsMaster, token]
  );

  const handleOpenAddModal = () => {
    setAddSelectedItem(null);
    setAddQty('');
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddSelectedItem(null);
    setAddQty('');
    isSubmittingAddRef.current = false;
    setAddSubmitting(false);
  };

  const handleAddToShortBooks = async () => {
    if (isSubmittingAddRef.current) return;

    const itemName = (addSelectedItem?.itemName || addSelectedItem?.label || '').trim();
    if (!addSelectedItem || !itemName) {
      toast.error('Please select or enter an item', { autoClose: 1000 });
      return;
    }

    if (!addQty || parseFloat(addQty) <= 0) {
      toast.error('Please enter a valid quantity', { autoClose: 1000 });
      return;
    }

    const itemNo = addSelectedItem.isNew || !addSelectedItem.value ? 0 : addSelectedItem.value;

    isSubmittingAddRef.current = true;
    setAddSubmitting(true);
    try {
      const payload = [
        {
          itemNo,
          itemName,
          qty: addQty,
          addedAt: new Date().toISOString(),
          isorderd: false,
        },
      ];

      const response = await axios.post(`${BASE_URL}${POST_SHORT_BOOKS_ITEMS}`, payload, {
        headers: {
          XApiKey: process.env.REACT_APP_API_KEY,
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        toast.success('Item added to short books successfully', { autoClose: 2000 });
        handleCloseAddModal();
        await getShortBooksData();
      } else {
        toast.error('Failed to add item to short books', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error adding to short books:', error);
      toast.error('Failed to add item to short books', { autoClose: 2000 });
    } finally {
      isSubmittingAddRef.current = false;
      setAddSubmitting(false);
    }
  };

  const getSuppliers = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'FilterText': '',
        },
      });

      if (response.status === 200) {
        const suppliersData = response.data.data || [];
        setSuppliers(suppliersData);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const getShortBooksData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SHORT_BOOKS_LIST}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const data = response.data.data || [];
        setShortBooksData(data);
        
        // Auto-select most profitable supplier (lowest purchasePrice) for each item
        const initialSelections = {};
        data.forEach((item) => {
          if (item.itemList && item.itemList.length > 0) {
            const bestSupplier = getMostProfitableSupplier(item.itemList);
            if (bestSupplier) {
              initialSelections[item.item_No] = bestSupplier;
            }
          }
        });
        setSelectedSuppliers(initialSelections);
        
        // Initialize quantities from data
        const initialQuantities = {};
        data.forEach((item) => {
          const qty = item.qty !== null && item.qty !== undefined ? item.qty :
                     item.quantity !== null && item.quantity !== undefined ? item.quantity :
                     item.Qty !== null && item.Qty !== undefined ? item.Qty : '';
          initialQuantities[item.item_No] = qty;
        });
        setQuantities(initialQuantities);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch short books data:', error);
      toast.error('Could not load short books data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search and supplier filter
  const filteredData = shortBooksData.filter((item) => {
    const matchesSearch = !searchQuery || 
      item.item_Name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSupplier = !supplierFilter || 
      (selectedSuppliers[item.item_No]?.supplierID === supplierFilter.value);
    
    return matchesSearch && matchesSupplier;
  });

  // Get supplier options for filter
  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.SupplierID,
    label: supplier.SupplierName
  }));

  // Handle quantity change for an item
  const handleQuantityChange = (itemNo, value) => {
    setQuantities(prev => ({
      ...prev,
      [itemNo]: value
    }));
  };

  // Handle supplier change for an item
  const handleSupplierChange = (itemNo, selectedOption) => {
    const item = shortBooksData.find(i => i.item_No === itemNo);
    if (!item) return;

    if (selectedOption && selectedOption.supplier) {
      // Use supplier from selectedOption if available (works for both with and without pricing)
      const supplierDetails = selectedOption.supplier;
      setSelectedSuppliers(prev => ({
        ...prev,
        [itemNo]: {
          supplierID: supplierDetails.supplierID,
          supplierName: supplierDetails.supplierName,
          packing: supplierDetails.packing || null,
          foc: supplierDetails.foc || null,
          purchasePrice: supplierDetails.purchasePrice || null
        }
      }));
    } else if (selectedOption && selectedOption.value && item.itemList) {
      // Fallback: Find the supplier details from itemList
      const supplierDetails = item.itemList.find(
        s => s.supplierID === selectedOption.value
      );
      
      if (supplierDetails) {
        setSelectedSuppliers(prev => ({
          ...prev,
          [itemNo]: {
            supplierID: supplierDetails.supplierID,
            supplierName: supplierDetails.supplierName,
            packing: supplierDetails.packing,
            foc: supplierDetails.foc,
            purchasePrice: supplierDetails.purchasePrice
          }
        }));
      }
    } else {
      // Clear selection
      setSelectedSuppliers(prev => {
        const updated = { ...prev };
        delete updated[itemNo];
        return updated;
      });
    }
  };

  // Get supplier options for a specific item - includes all suppliers
  const getItemSupplierOptions = (item) => {
    const itemListSuppliers = item.itemList || [];
    
    // Group suppliers by supplierID and find the one with lowest price for each
    const supplierMap = new Map();
    itemListSuppliers.forEach(s => {
      const supplierId = s.supplierID;
      const currentPrice = parseFloat(s.purchasePrice || 0);
      
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, s);
      } else {
        const existing = supplierMap.get(supplierId);
        const existingPrice = parseFloat(existing.purchasePrice || 0);
        // If current entry has lower price, or if existing has null/0 price, replace it
        if (currentPrice > 0 && (existingPrice === 0 || currentPrice < existingPrice)) {
          supplierMap.set(supplierId, s);
        } else if (currentPrice > 0 && existingPrice === 0) {
          supplierMap.set(supplierId, s);
        }
      }
    });
    
    // Combine all suppliers, prioritizing those with pricing info
    const allOptions = suppliers.map(supplier => {
      const supplierId = supplier.SupplierID;
      const hasPricing = supplierMap.has(supplierId);
      const supplierData = supplierMap.get(supplierId);
      
      return {
        value: supplierId,
        label: supplier.SupplierName,
        supplier: hasPricing ? supplierData : {
          supplierID: supplierId,
          supplierName: supplier.SupplierName,
          packing: null,
          foc: null,
          purchasePrice: null
        },
        hasPricing: hasPricing,
        purchasePrice: hasPricing ? (supplierData.purchasePrice || null) : null
      };
    });
    
    return allOptions;
  };

  // Handle checkbox toggle
  const handleCheckboxChange = (itemNo) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemNo)) {
        newSet.delete(itemNo);
      } else {
        newSet.add(itemNo);
      }
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    if (checked) {
      const allItemNos = new Set(filteredData.map(item => item.item_No));
      setSelectedItems(allItemNos);
    } else {
      setSelectedItems(new Set());
    }
  };

  // Handle bulk mark as ordered
  const handleBulkMarkAsOrdered = async () => {
    if (selectedItems.size === 0) {
      toast.error('Please select at least one item', { autoClose: 2000 });
      return;
    }

    // Validate all selected items have suppliers and quantities
    const itemsToSubmit = [];
    const missingData = [];

    selectedItems.forEach(itemNo => {
      const selected = selectedSuppliers[itemNo];
      const qty = quantities[itemNo];
      const item = shortBooksData.find(i => i.item_No === itemNo);

      if (!selected) {
        missingData.push(item?.item_Name || `Item ${itemNo}`);
      } else if (!qty || qty.trim() === '') {
        missingData.push(item?.item_Name || `Item ${itemNo}`);
      } else {
        itemsToSubmit.push({
          itemNo: itemNo,
          supplierID: selected.supplierID,
          qty: String(qty).trim()
        });
      }
    });

    if (missingData.length > 0) {
      toast.error(`Please select supplier and quantity for: ${missingData.join(', ')}`, {
        autoClose: 3000,
        icon: <FiAlertCircle className="text-red-500" />
      });
      return;
    }

    if (itemsToSubmit.length === 0) {
      toast.error('No valid items to submit', { autoClose: 2000 });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}${MARK_SHORT_BOOKS_ORDERS}`, itemsToSubmit, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        toast.success(`${itemsToSubmit.length} item(s) marked as ordered successfully`, {
          autoClose: 2000,
          icon: <FiCheckCircle className="text-green-500" />
        });
        
        // Remove submitted items from list
        const submittedItemNos = new Set(itemsToSubmit.map(item => item.itemNo));
        setShortBooksData(prev => prev.filter(item => !submittedItemNos.has(item.item_No)));
        setSelectedSuppliers(prev => {
          const updated = { ...prev };
          submittedItemNos.forEach(itemNo => delete updated[itemNo]);
          return updated;
        });
        setQuantities(prev => {
          const updated = { ...prev };
          submittedItemNos.forEach(itemNo => delete updated[itemNo]);
          return updated;
        });
        setSelectedItems(new Set());
      } else {
        toast.error('Failed to mark items as ordered', {
          autoClose: 2000,
          icon: <FiAlertCircle className="text-red-500" />
        });
      }
    } catch (error) {
      console.error('Failed to mark as ordered:', error);
      toast.error(error.response?.data?.message || 'Failed to mark items as ordered', {
        autoClose: 2000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle mark as ordered (single item - kept for backward compatibility)
  const handleMarkAsOrdered = async (itemNo) => {
    const selected = selectedSuppliers[itemNo];
    if (!selected) {
      toast.error('Please select a supplier first', { autoClose: 2000 });
      return;
    }

    const qty = quantities[itemNo];
    if (!qty || qty.trim() === '') {
      toast.error('Please enter a quantity', { autoClose: 2000 });
      return;
    }

    setLoading(true);
    try {
      const payload = [{
        itemNo: itemNo,
        supplierID: selected.supplierID,
        qty: String(qty).trim()
      }];

      const response = await axios.post(`${BASE_URL}${MARK_SHORT_BOOKS_ORDERS}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        toast.success(`${shortBooksData.find(i => i.item_No === itemNo)?.item_Name} marked as ordered`, {
          autoClose: 2000,
          icon: <FiCheckCircle className="text-green-500" />
        });
        
        // Remove from list after marking as ordered
        setShortBooksData(prev => prev.filter(item => item.item_No !== itemNo));
        setSelectedSuppliers(prev => {
          const updated = { ...prev };
          delete updated[itemNo];
          return updated;
        });
        setQuantities(prev => {
          const updated = { ...prev };
          delete updated[itemNo];
          return updated;
        });
        setSelectedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemNo);
          return newSet;
        });
      } else {
        toast.error('Failed to mark as ordered', {
          autoClose: 2000,
          icon: <FiAlertCircle className="text-red-500" />
        });
      }
    } catch (error) {
      console.error('Failed to mark as ordered:', error);
      toast.error(error.response?.data?.message || 'Failed to mark as ordered', {
        autoClose: 2000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveExportSupplier = (item) => {
    if (selectedSuppliers[item.item_No]) {
      return selectedSuppliers[item.item_No];
    }
    return getMostProfitableSupplier(item.itemList);
  };

  const buildExportRows = () =>
    filteredData.map((item, index) => {
      const supplier = resolveExportSupplier(item);
      const qty = quantities[item.item_No] !== undefined ? quantities[item.item_No] : '';
      return {
        siNo: index + 1,
        productName: item.item_Name || '',
        qty: qty !== '' && qty !== null && qty !== undefined ? String(qty) : '-',
        addedBy: item.userName || '-',
        addedAt: formatDate(item.addedAt) || '-',
        supplierName: supplier?.supplierName || '-',
        purchasePrice:
          supplier?.purchasePrice !== null && supplier?.purchasePrice !== undefined
            ? parseFloat(supplier.purchasePrice).toFixed(2)
            : '-',
        packing: supplier?.packing || '-',
        foc: supplier?.foc || '-',
      };
    });

  const exportToExcel = () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        toast.error('No short book items to export', { autoClose: 2000 });
        return;
      }

      const rows = buildExportRows();
      const excelData = rows.map((row) => ({
        'SI No': row.siNo,
        'Product Name': row.productName,
        Qty: row.qty,
        'Added By': row.addedBy,
        'Added At': row.addedAt,
        Supplier: row.supplierName,
        'Purchase Price': row.purchasePrice,
        Packing: row.packing,
        FOC: row.foc,
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: 8 },
        { wch: 35 },
        { wch: 10 },
        { wch: 20 },
        { wch: 18 },
        { wch: 25 },
        { wch: 14 },
        { wch: 12 },
        { wch: 10 },
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Short Books');

      const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      XLSX.writeFile(wb, `Short_Books_${dateStr}.xlsx`);
      toast.success('Excel file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel', { autoClose: 2000 });
    }
  };

  const exportToPDF = () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        toast.error('No short book items to export', { autoClose: 2000 });
        return;
      }

      const rows = buildExportRows();
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Short Books Report', pageWidth / 2, 15, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      if (supplierFilter?.label) {
        doc.text(`Supplier Filter: ${supplierFilter.label}`, pageWidth / 2, 22, { align: 'center' });
      }
      if (searchQuery) {
        doc.text(`Search: ${searchQuery}`, pageWidth / 2, supplierFilter?.label ? 28 : 22, { align: 'center' });
      }
      const metaY = supplierFilter?.label && searchQuery ? 34 : supplierFilter?.label || searchQuery ? 28 : 22;
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, metaY, { align: 'center' });
      doc.text(`Total Items: ${rows.length}`, pageWidth / 2, metaY + 6, { align: 'center' });

      const tableData = rows.map((row) => [
        row.siNo,
        row.productName,
        row.qty,
        row.addedBy,
        row.addedAt,
        row.supplierName,
        row.purchasePrice !== '-' ? `₹${row.purchasePrice}` : '-',
        row.packing,
        row.foc,
      ]);

      autoTable(doc, {
        head: [['SI', 'Product Name', 'Qty', 'Added By', 'Added At', 'Supplier', 'Price', 'Packing', 'FOC']],
        body: tableData,
        startY: metaY + 12,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [99, 102, 241],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
        },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { top: metaY + 12, left: 10, right: 10 },
      });

      const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      doc.save(`Short_Books_${dateStr}.pdf`);
      toast.success('PDF file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF', { autoClose: 2000 });
    }
  };

  // Format date with time
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      <div className="flex-1 h-full flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200 px-3 py-2 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Short Books
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Supplier Filter */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-black whitespace-nowrap">Filter by Supplier:</label>
                <div className="w-64">
                  <Select
                    options={supplierOptions}
                    placeholder="All Suppliers"
                    isClearable
                    isSearchable
                    value={supplierFilter}
                    onChange={(selectedOption) => {
                      setSupplierFilter(selectedOption);
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              {supplierFilter && (
                <button
                  onClick={() => {
                    setSupplierFilter(null);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center h-[38px]"
                  title="Clear filter"
                >
                  <FiX className="mr-1" size={12} /> Clear
                </button>
              )}

              {/* Global Submit Button */}
              {selectedItems.size > 0 && (
                <button
                  onClick={handleBulkMarkAsOrdered}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center h-[38px]"
                  title={`Mark ${selectedItems.size} item(s) as ordered`}
                >
                  <FiCheck className="mr-2" size={14} />
                  Mark {selectedItems.size} as Ordered
                </button>
              )}

              <button
                type="button"
                onClick={exportToExcel}
                className="px-4 py-1.5 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors flex items-center h-[38px]"
                title="Export to Excel"
              >
                <FiDownload className="mr-2" size={14} />
                Excel
              </button>

              <button
                type="button"
                onClick={exportToPDF}
                className="px-4 py-1.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex items-center h-[38px]"
                title="Export to PDF"
              >
                <FiDownload className="mr-2" size={14} />
                PDF
              </button>

              <button
                type="button"
                onClick={handleOpenAddModal}
                className="px-4 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center h-[38px]"
              >
                <FiPlus className="mr-2" size={14} />
                Add Item
              </button>

              <button 
                onClick={getShortBooksData}
                className="px-2 py-1.5 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center h-[38px]"
              >
                <FiRefreshCw className="mr-2" size={14} /> Refresh
              </button>
            </div>
          </div>
        </div>

        <AddShortBookModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          selectedItem={addSelectedItem}
          onItemChange={setAddSelectedItem}
          qty={addQty}
          setQty={setAddQty}
          onSubmit={handleAddToShortBooks}
          loadItemOptions={loadItemOptions}
          isSubmitting={addSubmitting}
        />
        
        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search Bar */}
          <div className="p-2 border-b border-gray-200 bg-white">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black">
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="flex-1 overflow-auto bg-white">
            {filteredData.length > 0 ? (
              <div className="p-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-2 text-center border border-gray-300 font-semibold text-black whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={filteredData.length > 0 && filteredData.every(item => selectedItems.has(item.item_No))}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                            title="Select all"
                          />
                        </th>
                        <th className="px-2 py-2 text-center border border-gray-300 font-semibold text-black whitespace-nowrap">SI No</th>
                        <th className="px-2 py-2 text-left border border-gray-300 font-semibold text-black whitespace-nowrap">Product Name</th>
                        <th className="px-2 py-2 text-center border border-gray-300 font-semibold text-black whitespace-nowrap">Qty</th>
                        <th className="px-2 py-2 text-left border border-gray-300 font-semibold text-black whitespace-nowrap">Added By</th>
                        <th className="px-2 py-2 text-left border border-gray-300 font-semibold text-black whitespace-nowrap">Supplier</th>
                        <th className="px-2 py-2 text-center border border-gray-300 font-semibold text-black whitespace-nowrap">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {filteredData.map((item, index) => {
                        const selectedSupplier = selectedSuppliers[item.item_No];
                        const hasSuppliers = item.itemList && item.itemList.length > 0;
                        const supplierOptions = getItemSupplierOptions(item);
                        
                        return (
                          <tr
                            key={item.item_No}
                            className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${selectedItems.has(item.item_No) ? 'bg-indigo-50' : ''}`}
                          >
                            {/* Checkbox */}
                            <td className="px-2 py-2 border border-gray-200 text-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(item.item_No)}
                                onChange={() => handleCheckboxChange(item.item_No)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                              />
                            </td>
                            
                            {/* Serial Number */}
                            <td className="px-2 py-2 border border-gray-200 text-black font-medium text-center">
                              {index + 1}
                            </td>
                            
                            {/* Product Name */}
                            <td className="px-2 py-2 border border-gray-200 text-black font-semibold">
                              {item.item_Name}
                            </td>
                            
                            {/* Quantity */}
                            <td className="px-2 py-2 border border-gray-200 text-center">
                              <input
                                type="text"
                                value={quantities[item.item_No] !== undefined ? quantities[item.item_No] : ''}
                                onChange={(e) => handleQuantityChange(item.item_No, e.target.value)}
                                className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-black text-sm"
                                placeholder="-"
                              />
                            </td>
                            
                            {/* Added User Name with Date */}
                            <td className="px-2 py-2 border border-gray-200 text-black">
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{item.userName || '-'}</span>
                                <span className="text-xs text-gray-500 mt-0.5">
                                  {formatDate(item.addedAt)}
                                </span>
                              </div>
                            </td>
                            
                            {/* Supplier Dropdown */}
                            <td className="px-2 py-2 border border-gray-200">
                              {suppliers.length > 0 ? (
                                <div className="min-w-[200px]">
                                  <Select
                                    options={supplierOptions}
                                    placeholder="Select Supplier"
                                    isSearchable
                                    value={selectedSupplier ? supplierOptions.find(
                                      opt => opt.value === selectedSupplier.supplierID
                                    ) : null}
                                    onChange={(selectedOption) => {
                                      if (selectedOption) {
                                        const supplierDetails = selectedOption.supplier;
                                        handleSupplierChange(item.item_No, {
                                          value: supplierDetails.supplierID,
                                          supplier: supplierDetails
                                        });
                                      } else {
                                        handleSupplierChange(item.item_No, null);
                                      }
                                    }}
                                    formatOptionLabel={({ label, hasPricing, purchasePrice }) => (
                                      <div className="flex items-center justify-between w-full">
                                        <span className="flex-1">{label}</span>
                                        {hasPricing && purchasePrice !== null && purchasePrice !== undefined ? (
                                          <span className="text-xs font-semibold text-indigo-600 ml-2 whitespace-nowrap">
                                            ₹{parseFloat(purchasePrice).toFixed(2)}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">(No pricing)</span>
                                        )}
                                      </div>
                                    )}
                                    components={{
                                      SingleValue: CustomSingleValue
                                    }}
                                    styles={{
                                      ...customSelectStyles,
                                      control: (provided, state) => ({
                                        ...customSelectStyles.control(provided, state),
                                        minHeight: '36px',
                                        height: '36px',
                                      }),
                                      valueContainer: (provided) => ({
                                        ...provided,
                                        padding: '2px 8px',
                                        height: '36px',
                                      }),
                                      input: (provided) => ({
                                        ...provided,
                                        margin: '0px',
                                        padding: '0px',
                                      }),
                                      indicatorsContainer: (provided) => ({
                                        ...provided,
                                        height: '36px',
                                      }),
                                    }}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">No suppliers</span>
                              )}
                            </td>
                            
                            {/* Mark as Ordered Button */}
                            <td className="px-2 py-2 border border-gray-200 text-center">
                              {selectedSupplier ? (
                                <button
                                  onClick={() => handleMarkAsOrdered(item.item_No)}
                                  className="px-2 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-xs rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5 whitespace-nowrap"
                                >
                                  <FiCheck size={12} />
                                  Mark as Ordered
                                </button>
                              ) : (
                                <span className="text-gray-400 text-xs">Select supplier</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 py-10">
                <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black">
                  <FiFileText size={32} />
                </div>
                <h3 className="text-lg font-medium text-black">No items found</h3>
                <p className="text-sm text-black mt-1">
                  {searchQuery || supplierFilter 
                    ? 'Try adjusting your search or filters' 
                    : 'No items in short books'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAuth(ShortBooks);
